import {Component, computed, signal, Signal, WritableSignal} from '@angular/core';
import {RouterModule} from '@angular/router';
import {combineLatest} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';
import {gqlLoad, LoadState, LOADING} from '../../../graphql/gql-load';
import {LoadRowComponent} from '../../../graphql/load-row.component';
import {Folder, Organization, Query} from '../../../types-repository';
import {Apollo} from 'apollo-angular';
import {GET_ROOTS} from '../../../queries-repository';
import {LogoAreaComponent} from '../../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import { AsyncPipe } from '@angular/common';
import {FileFolderLinkPipe} from '../../file-folder-link.pipe';
import {ContextService} from '../../../services/context.service';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {OrgResolverPipe} from '../../../pipes/org-resolver-pipe';
import {DateComponent} from '../../../date/date.component';

@Component({
  selector: 'app-home-component',
  templateUrl: 'home.component.html',
  styleUrls: ['home.component.scss'],
  imports: [
    LogoAreaComponent,
    HeadlineSectionComponent,
    RouterModule,
    FileFolderLinkPipe,
    OrgResolverPipe,
    DateComponent,
    LoadRowComponent
]
})
export class HomeComponent {

  /** The roots query: loading, the sorted roots, or why they could not be loaded. */
  state: Signal<LoadState<Folder[]>>;
  /** True until the rows can be shown (still loading, or failed). */
  loading = computed(() => this.state().loading || !!this.state().error);
  roots = computed(() => this.state().data ?? []);
  organization: Signal<Organization>;

  /** Bumped by refresh() to re-run the query without the organization having changed. */
  private reload: WritableSignal<number> = signal(0);

  constructor(private apollo: Apollo,
              private organizationService: ContextService) {
    this.organization = this.organizationService.organization
    this.state = toSignal(
      combineLatest([toObservable(this.organization), toObservable(this.reload)]).pipe(
        filter(([_org]) => !!_org),
        // network-only, not the default cache-first: the roots are cached per organization, so
        // switching away and back served a stale list -- and the list genuinely changes underneath
        // us, since a supplier's roots are the delivery directories that exist right now.
        // gqlLoad inside the switchMap: a failed load shows its error row and the next refresh() loads again.
        switchMap(([_org]) => gqlLoad(this.apollo.query<Query>({query: GET_ROOTS(_org.id), fetchPolicy: 'network-only'}),
          res => [...res.data.roots].sort((a, b) => a.name.localeCompare(b.name)))),
      ),
      {initialValue: LOADING}
    )
  }

  refresh() {
    this.reload.update(n => n + 1);
  }
}
